import React, { useRef, useEffect } from 'react';
import ModalStyles, { GlobalStyle } from './Modal.styled';

import {
  scrollLock,
  scrollUnlock,
  scrollLocked,
} from 'pubweb-smokey/dist/utils/utils';

import XSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/x.svg';

const modalStack: React.RefObject<HTMLDivElement>[] = [];

interface ModalProps {
  children: React.ReactNode;
  onClose?: (e: React.SyntheticEvent | Event) => void;
}

const Modal = ({ children, onClose }: ModalProps) => {
  const modalContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollLocked()) {
      scrollLock();

      return () => {
        scrollUnlock();
      };
    }
  }, []);

  // Handle exit using escape key via eventListener. If enter key is processed here for closing modal, we have problems
  // when enter key press is required for other things like a button (e.g. login modal button).
  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && onClose) {
      if (modalStack[modalStack.length - 1] == modalContainerRef) {
        onClose(e);
      }
    }
  };

  // Handle exit using enter key via onKeyDown.
  const handleExit = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && onClose) {
      onClose(e);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    modalStack.push(modalContainerRef);
    if (modalContainerRef.current) {
      const focusableElements =
        modalContainerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
      focusableElements[0]?.focus();
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      modalStack.splice(modalStack.indexOf(modalContainerRef), 1);
    };
  }, []);

  return (
    <ModalStyles role="dialog" aria-modal="true" className="modal-component">
      <GlobalStyle />
      <div className="modal-filter" onClick={onClose}></div>
      <div className="modal-container" ref={modalContainerRef}>
        <div className="close-modal">
          <span tabIndex={0} onClick={onClose} onKeyDown={handleExit}>
            <XSvg viewBox="0 0 16 16" />
          </span>
        </div>
        <div className="modal-content">{children}</div>
      </div>
    </ModalStyles>
  );
};

export default Modal;
